import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TimeAgoPipe } from '@ui-lib/pipes';
import { Observable, Subscription } from 'rxjs';
import { GpAddressesApiService } from '../../../api/admin/orders/gpaddresses/gp-addresses.service';
import { GpLettersService } from '../../../services/gp-letters/gp-letters.service';
import { GpAddressResponse, QueryParams, QueryParamsFilter } from '../../../types/gp-address-request-response';
import { GpAddressStatus, GpAddressVerifiedStatus, IncompleteGpAddressTableData } from '../../../types/gp-letters';
import { EmitFilterChangeParams, FiltersComponent } from '../components/filters/filters.component';

@Component({
    selector: 'pending-gp-letters',
    templateUrl: './pending-gp-letters.component.html',
    styleUrl: './pending-gp-letters.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
      // TODO: Angular 19 WARNINGS: Unused import. Delete if unnecessary
      // MatFormField,
      // MatLabel,
      // MatSelect,
      // MatOption,
      // MultiSelectComponent,
      // MatChip,
      // MatChipInput,
      // MatChipListbox,
      // MatChipGrid,
      // FollowupComponent,
      // StatusUpdateComponent,
      // AsyncPipe,
      //__________________________
      CommonModule,
      MatTableModule,
      MatPaginatorModule,
      FormsModule,
      ReactiveFormsModule,
      MatFormFieldModule,
      MatInputModule,
      MatChipsModule,
      MatIcon,
      MatButtonModule,
      MatIconModule,
      TimeAgoPipe,
      FiltersComponent,
    ]
})
export class PendingGpLettersComponent implements OnDestroy {
  //TODO url parametric pagination search
  //private route = inject(ActivatedRoute);
  /* ngOnInit() {
    this.route.paramMap.subscribe(params => {
      this.paginator.pageSize = Number(params.get('limit'));
      this.paginator.pageIndex = Number(params.get('number'));
    });
  } */

  // injections
  private gpAddressesApiService = inject(GpAddressesApiService);
  private gpLettersService = inject(GpLettersService);
  private ref = inject(ChangeDetectorRef);

  displayedColumns: string[] = [
    'visit',
    'service',
    'medications',
    'gp_address',
    'visit_completed',
    'print',
    'markSelectAsPrinted',
    'checkbox',
  ];
  dataSource = new MatTableDataSource<IncompleteGpAddressTableData>([]);
  searchTerm = '';
  paginator: PageEvent = { pageIndex: 0, pageSize: 20, length: 0 };
  pageSizeOptions = [10, 20, 50, 75, 100];
  filter: QueryParamsFilter = {};
  allItemsSelected = false;
  selectedItemsQty = 0;
  protected readonly GpAddressStatus = GpAddressStatus;
  protected readonly GpAddressVerifiedStatus = GpAddressVerifiedStatus;
  private subs = new Subscription();

  onFilterChanged({ filter, searchTerm }: EmitFilterChangeParams) {
    this.searchTerm = searchTerm;
    this.filter = filter;
    this.getPendingGpLetters(this.paginator, filter, searchTerm);
  }

  handlePageEvent(event: PageEvent) {
    this.paginator = { ...event };
    this.getPendingGpLetters(this.paginator, this.filter, this.searchTerm);
  }

  getPendingGpLetters(paginator: PageEvent, filter: QueryParamsFilter, search?: string) {
    const queryParams: QueryParams = {
      filter: {
        ...this.filter,
        ...filter,
      },
      page: {
        number: paginator.pageIndex + 1,
        limit: paginator.pageSize,
      },
      search: search,
      cache: 0,
    };
    this.subs.add(
      this.getPendingGpLetters$(queryParams).subscribe(response => {
        this.handleResponse(response);
      }),
    );
  }

  handleResponse(response: GpAddressResponse) {
    this.dataSource = new MatTableDataSource<IncompleteGpAddressTableData, MatPaginator>(
      this.gpLettersService.transformJsonApiData(response),
    );
    this.paginator.length = response.meta.pagination.total;
    this.ref.markForCheck();
  }

  getPendingGpLetters$(queryParams: QueryParams): Observable<GpAddressResponse> {
    return this.gpAddressesApiService.getPendingGpLetters(queryParams);
  }

  getPrintLetters$(visitIds: string[]): Observable<Blob> {
    return this.gpAddressesApiService.postPrintLetters$(visitIds);
  }

  getFullAddress(element: IncompleteGpAddressTableData): string {
    return `${element.surgery_name}, ${element.line_1}, ${element.town},
				${element.postcode}`;
  }

  putMarkAsPrinted$(visitIds: string[]) {
    return this.gpAddressesApiService.putMarkAsPrinted$(visitIds);
  }

  selectItem(
    dataSource: MatTableDataSource<IncompleteGpAddressTableData, MatPaginator>,
    element: IncompleteGpAddressTableData,
  ) {
    element.selected = !element.selected;
    this.updateSelectedItems(dataSource);
  }

  selectAllElement(dataSource: MatTableDataSource<IncompleteGpAddressTableData, MatPaginator>) {
    this.allItemsSelected = !this.allItemsSelected;
    dataSource.data.forEach(element => {
      element.selected = this.allItemsSelected;
    });
    this.updateSelectedItems(dataSource);
  }

  updateSelectedItems(dataSource: MatTableDataSource<IncompleteGpAddressTableData, MatPaginator>) {
    this.selectedItemsQty = dataSource.data.filter(item => item.selected).length;
    dataSource.data.length === this.selectedItemsQty ? (this.allItemsSelected = true) : (this.allItemsSelected = false);
  }

  printLetters(
    dataSource: MatTableDataSource<IncompleteGpAddressTableData, MatPaginator>,
    element?: IncompleteGpAddressTableData,
  ) {
    const printLettersIds = element
      ? [element.visit_id]
      : dataSource.data.filter(item => item.selected).map(item => item.visit_id);
    this.subs.add(
      this.getPrintLetters$([...printLettersIds]).subscribe(data => {
        this.handlePdf(data);
      }),
    );
  }

  maskAsPrintedLetters(
    dataSource: MatTableDataSource<IncompleteGpAddressTableData, MatPaginator>,
    element?: IncompleteGpAddressTableData,
  ) {
    const visitIds: string[] = element
      ? [element.visit_id]
      : dataSource.data.filter(item => item.selected).map(item => item.visit_id);
    this.subs.add(
      this.putMarkAsPrinted$(visitIds).subscribe(() => {
        if (element) {
          element.printed = true;
        } else {
          dataSource.data.forEach(item => {
            if (item.selected) {
              item.printed = true;
            }
          });
        }
        this.ref.markForCheck();
      }),
    );
  }

  handlePdf(blob: Blob) {
    const fileURL = window.URL.createObjectURL(blob);
    const tab = window.open();
    tab ? (tab.location.href = fileURL) : null;
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
